
import React, { ReactNode, useEffect, useId, useRef, useState } from "react";
import { useUrlPageState } from "../utilities/dataTablePagination";
import { userAPI } from "../UserAPI";
import Section from "../components/Section";
import Metric from "../components/Metric";
import { useAdministrationAPI } from "../AdministrationAPI";
import { useModalContext } from "../components/ModalProvider";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import { useCustomerTheme } from "../Theme";
import SecurityPanel from "./SecurityPanel";

import type { Column, ColumnDef } from "@tanstack/react-table";
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    Database,
    Layers,
    Loader2,
    RefreshCw,
} from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({
    column,
    children,
}: {
    column: Column<any, unknown>;
    children: ReactNode;
}) {
    const sorted = column.getIsSorted();

    return (
        <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
            onClick={() => column.toggleSorting(sorted === "asc")}
        >
            {children}
            {sorted === "asc" ? (
                <ArrowUp aria-hidden className="h-3.5 w-3.5" />
            ) : sorted === "desc" ? (
                <ArrowDown aria-hidden className="h-3.5 w-3.5" />
            ) : (
                <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
            )}
        </Button>
    );
}

/** Leerzustand einer Tabelle: Überschrift, Erklärung und Handlungsaufforderung. */
function TableEmptyState({
    title,
    hint,
    action,
}: {
    title: string;
    hint?: string;
    action?: ReactNode;
}) {
    return (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
            <div className="text-sm font-semibold text-foreground">{title}</div>
            {hint ? <div className="max-w-prose text-sm text-muted-foreground">{hint}</div> : null}
            {action}
        </div>
    );
}

/** Aktualisieren-Schaltfläche der Abschnitte (Symbol + Beschriftung für Screenreader). */
function RefreshButton({
    label,
    disabled,
    onClick,
}: {
    label: string;
    disabled?: boolean;
    onClick: () => void;
}) {
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label={label}
                        disabled={disabled}
                        onClick={onClick}
                    >
                        {disabled ? (
                            <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                        ) : (
                            <RefreshCw aria-hidden className="h-4 w-4" />
                        )}
                    </Button>
                </TooltipTrigger>
                <TooltipContent>{label}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

const MonitoringPanel: React.FC = () => {
    const administrationAPI = useAdministrationAPI();
    const { pushMessage, pushSuccess } = useModalContext() as any;
    const fieldId = useId();
    const agenticServicesPagination = useUrlPageState('agenticServicesPage');

    const [agenticData, setAgenticData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [usageMetrics, setUsageMetrics] = useState<any>(null);
    const [zoningStats, setZoningStats] = useState<any>(null);
    const [_zoningJobs, setZoningJobs] = useState<any>(null);
    const [_zoningSVG, _setZoningSVG] = useState<string | null>(null);
    const [zoningGrid, setZoningGrid] = useState<any>(null);
    const [tileCounts, setTileCounts] = useState<Record<string, number>>({});
    const [germanyMapPaths, setGermanyMapPaths] = useState<string>("");
    const [showMapOverlay, setShowMapOverlay] = useState(false);
    const [lastSVGUpdate, setLastSVGUpdate] = useState<Date | null>(null);
    const [loadingZoning, setLoadingZoning] = useState(false);
    const [_loadingJobs, setLoadingJobs] = useState(false);
    const [loadingDbJobs, setLoadingDbJobs] = useState(false);
    const [zoningDbJobsError, setZoningDbJobsError] = useState<string | null>(null);
    const [dbJobsLoadedOnce, setDbJobsLoadedOnce] = useState(false);
    const [watchJobUpdatedAt, setWatchJobUpdatedAt] = useState<string | null>(null);
    const [loopJobs, setLoopJobs] = useState<Record<string, any>>({});
    const [loadingLoopJobs, setLoadingLoopJobs] = useState(false);
    const [loopJobsError, setLoopJobsError] = useState<string | null>(null);
    const [loopJobsLoadedOnce, setLoopJobsLoadedOnce] = useState(false);
    const watchRequestIdRef = useRef(0);
    const loadingLoopJobsRef = useRef(false);
    const [watchJobId, setWatchJobId] = useState<string | null>(null);
    const [watchJob, setWatchJob] = useState<any>(null);
    const [showWatchDialog, setShowWatchDialog] = useState(false);
    const [watchContextCommand, _setWatchContextCommand] = useState<string>("");
    const [loadingSVG, setLoadingSVG] = useState(false);
    const [zoningReloadAt, setZoningReloadAt] = useState<Date | null>(null);
    const [_zoningReloadSource, setZoningReloadSource] = useState<string>("");
    const [filter, setFilter] = useState("");
    const { t } = useTranslation();
    const { theme } = useCustomerTheme();
    const [showExamplesDialog, setShowExamplesDialog] = useState(false);
    const [currentExamples, setCurrentExamples] = useState<string[]>([]);
    const [tab, setTab] = useState("usage");
    const loadingZoningRef = useRef(false);
    const loadingSVGRef = useRef(false);

    const filterFieldId = `${fieldId}-agentic-filter`;

    const agenticColumns: ColumnDef<any>[] = [
        {
            id: 'name',
            size: 350,
            accessorFn: (row: any) => row.name,
            header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
            cell: ({ row }) => <div className="min-w-0 break-words text-sm">{row.original.name}</div>,
        },
        {
            id: 'type',
            size: 150,
            accessorFn: (row: any) => row.type,
            header: ({ column }) => <SortableHeader column={column}>Type</SortableHeader>,
            cell: ({ row }) => {
                switch (row.original.type) {
                    case 'intent':
                        return <Badge>Internes Werkzeug</Badge>;
                    case 'mcpTool':
                        return <Badge variant="secondary">MCP-Werkzeug</Badge>;
                    case 'knowledgeBase':
                        return <Badge variant="outline">Wissensbasis</Badge>;
                    default:
                        return <div className="text-sm">{row.original.type}</div>;
                }
            },
        },
        {
            id: 'provider',
            size: 200,
            accessorFn: (row: any) => row.provider.name,
            header: ({ column }) => <SortableHeader column={column}>Provider</SortableHeader>,
            cell: ({ row }) => (
                <div className="min-w-0">
                    <div className="min-w-0 break-words text-sm font-bold">{row.original.provider.name}</div>
                    <div className="min-w-0 break-all text-sm">
                        <a
                            className="underline underline-offset-2"
                            href={row.original.provider.url}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {row.original.provider.url}
                        </a>
                    </div>
                </div>
            ),
        },
        {
            id: 'description',
            accessorFn: (row: any) => row.description,
            header: ({ column }) => <SortableHeader column={column}>Description</SortableHeader>,
            enableSorting: false,
            cell: ({ row }) => (
                <div className="flex min-w-0 flex-col items-start gap-2">
                    <div className="min-w-0 break-words text-sm">{row.original.description}</div>
                    {row.original.examples && row.original.examples.length > 0 ? (
                        <button
                            type="button"
                            className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            onClick={() => {
                                setCurrentExamples(row.original.examples);
                                setShowExamplesDialog(true);
                            }}
                        >
                            ({row.original.examples.length} Beispielanfrage(n))
                        </button>
                    ) : (
                        <div className="text-sm text-muted-foreground">(Keine Beispielanfragen)</div>
                    )}
                </div>
            ),
        },
    ];

    const loopJobDefinitions = [
        {
            type: 'orchestratorDiscoveryLoop',
            label: 'Discovery Loop',
            buildDetails: (stats: any) => `Targets: ${stats.targetsCount ?? 0}, Sources: ${stats.sourcesFound ?? 0}`,
        },
        {
            type: 'orchestratorVerificationLoop',
            label: 'Verification Loop',
            buildDetails: (stats: any) => `Batch: ${stats.lastBatchSize ?? 0}, Verified: ${stats.lastVerified ?? 0}`,
        },
        {
            type: 'orchestratorExtractionLoop',
            label: 'Extraction Loop',
            buildDetails: (stats: any) => `Batch: ${stats.lastBatchSize ?? 0}, Extracted: ${stats.lastExtracted ?? 0}`,
        },
        {
            type: 'orchestratorConsolidationLoop',
            label: 'Consolidation Loop',
            buildDetails: (stats: any) => `Plans: ${stats.planProcessed ?? 0}/${stats.planUpdated ?? 0}, Features: ${stats.featureProcessed ?? 0}/${stats.featureUpdated ?? 0}, Masters: ${stats.mastersUpdated ?? 0}`,
        },
    ];

    const loopJobColumns: ColumnDef<any>[] = [
        {
            id: 'loop',
            size: 220,
            enableSorting: false,
            header: () => 'Loop',
            cell: ({ row }) => (
                <div className="flex min-w-0 flex-col">
                    <div className="min-w-0 break-words text-sm font-bold">{row.original.label}</div>
                    <div className="min-w-0 break-all text-xs text-muted-foreground">{row.original.type}</div>
                </div>
            ),
        },
        {
            id: 'status',
            size: 120,
            enableSorting: false,
            header: () => 'Status',
            cell: ({ row }) => <div className="text-xs">{row.original.status || '-'}</div>,
        },
        {
            id: 'lastRunAt',
            size: 180,
            enableSorting: false,
            header: () => 'Letzter Lauf',
            cell: ({ row }) => <div className="whitespace-nowrap text-xs">{row.original.lastRunAt || '-'}</div>,
        },
        {
            id: 'lastHeartbeatAt',
            size: 180,
            enableSorting: false,
            header: () => 'Heartbeat',
            cell: ({ row }) => <div className="whitespace-nowrap text-xs">{row.original.lastHeartbeatAt || '-'}</div>,
        },
        {
            id: 'lastCycleDuration',
            size: 110,
            enableSorting: false,
            header: () => 'Dauer',
            cell: ({ row }) => <div className="whitespace-nowrap text-xs">{row.original.lastCycleDuration || '-'}</div>,
        },
        {
            id: 'cycles',
            size: 80,
            enableSorting: false,
            header: () => 'Zyklen',
            cell: ({ row }) => <div className="text-xs tabular-nums">{row.original.cycles}</div>,
        },
        {
            id: 'errors',
            size: 80,
            enableSorting: false,
            header: () => 'Errors',
            cell: ({ row }) => <div className="text-xs tabular-nums">{row.original.errors}</div>,
        },
        {
            id: 'details',
            enableSorting: false,
            header: () => 'Details',
            cell: ({ row }) => (
                <div className="min-w-0 break-words text-xs text-muted-foreground">
                    {row.original.details}
                    {row.original.lastError ? ` | lastError: ${row.original.lastError}` : ''}
                </div>
            ),
        },
    ];

    const DbJobLogsView = ({ data }: { data: any }) => {
        if (!data) {
            return (
                <div className="max-h-[300px] min-w-0 overflow-auto whitespace-pre-wrap break-words rounded-md bg-foreground p-4 font-mono text-[11px] text-background">
                    (Warten auf DB-Job-Daten...)
                </div>
            );
        }

        const stats = data.stats || {};
        const checkpoint = stats.checkpoint || {};
        const layers = checkpoint.layers || {};
        const layerResults = stats.layerResults || stats.lastSourceLayerResults || {};
        const lastHeartbeat = checkpoint.lastHeartbeatAt || stats.lastHeartbeatAt || data.updatedAt;
        const layersDone = stats.layersDone ?? 0;
        const layersFailed = stats.layersFailed ?? 0;
        const countUpserted = stats.countUpserted ?? stats.lastSourcePlansExtracted ?? stats.lastSourceFeaturesExtracted ?? 0;
        const sourceId = data.parameter?.sourceId || '-';
        const mode = data.parameter?.mode || '-';
        const currentSource = stats.currentSource || '-';
        const currentLayer = stats.currentLayer || '-';
        const lastSourceDurationMs = stats.lastSourceDurationMs ?? stats.durationMs ?? '-';

        const layerEntries = Object.entries(layers)
            .sort((a: any, b: any) => (b[1]?.featuresUpserted || 0) - (a[1]?.featuresUpserted || 0))
            .slice(0, 12);

        const resultEntries = Object.entries(layerResults)
            .sort((a: any, b: any) => (b[1] as number) - (a[1] as number))
            .slice(0, 20);

        const lines: string[] = [
            `Job ID: ${data._id}`,
            `Type: ${data.type || '-'}`,
            `Status: ${data.status || '-'}`,
            `Source: ${sourceId}`,
            `Mode: ${mode}`,
            `Current source: ${currentSource}`,
            `Current layer: ${currentLayer}`,
            `Created: ${data.createdAt || '-'}`,
            `Updated: ${data.updatedAt || '-'}`,
            `Last heartbeat: ${lastHeartbeat || '-'}`,
            `Upserted: ${countUpserted}`,
            `Last source duration (ms): ${lastSourceDurationMs}`,
            `Layers done/failed: ${layersDone}/${layersFailed}`,
            '',
            'Layer results (feature counts):',
        ];

        if (resultEntries.length === 0) {
            lines.push('  (Keine Layer-Ergebnisse im Job)');
        } else {
            resultEntries.forEach(([name, value]: any) => {
                lines.push(`  ${name}: ${value}`);
            });
        }

        lines.push('', 'Top layers (checkpoint progress):');

        if (layerEntries.length === 0) {
            lines.push('  (Keine Layer-Daten im Checkpoint)');
        } else {
            layerEntries.forEach(([name, info]: any) => {
                const status = info?.status || '-';
                const pagesDone = info?.pagesDone ?? 0;
                const upserted = info?.featuresUpserted ?? 0;
                const nextStart = info?.nextStartIndex ?? 0;
                lines.push(`  ${name}: status=${status} pages=${pagesDone} upserted=${upserted} nextStart=${nextStart}`);
                if (info?.lastError) {
                    lines.push(`    lastError: ${info.lastError}`);
                }
            });
        }

        return (
            <div className="max-h-[300px] min-w-0 overflow-auto whitespace-pre-wrap break-words rounded-md bg-foreground p-4 font-mono text-[11px] text-background">
                {lines.join('\n')}
            </div>
        );
    };


    const loadAgenticServices = () => {
        setLoading(true);
        userAPI.getAgenticServices()
            .then(setAgenticData)
            .catch(() => setAgenticData([]))
            .finally(() => setLoading(false));
    };

    const loadUsageMetrics = () => {
        setLoading(true);
        userAPI.getUsageMetrics()
            .then(setUsageMetrics)
            .catch(() => setUsageMetrics(null))
            .finally(() => setLoading(false));
    };

    const loadZoningStats = () => {
        console.log('loadZoningStats: starting');
        setLoadingZoning(true);
        userAPI.getZoningBacklog()
            .then((stats: any) => {
                console.log('loadZoningStats: Zoning Stats Received:', stats);
                if (stats && typeof stats === "object") {
                    setZoningStats({ ...stats, error: null });
                } else {
                    setZoningStats({ stats: {}, total: 0, error: null });
                }
            })
            .catch(error => {
                console.error('loadZoningStats: Failed to load zoning stats:', error);
                setZoningStats((prev: any) => prev
                    ? { ...prev, error: error.message || 'Unknown error' }
                    : { stats: {}, total: 0, error: error.message || 'Unknown error' }
                );
            })
            .finally(() => {
                console.log('loadZoningStats: finally reached');
                setLoadingZoning(false);
            });
    };

    const loadZoningJobs = () => {
        setLoadingJobs(true);
        userAPI.getZoningJobs()
            .then(data => {
                setZoningJobs(data);
            })
            .catch(error => {
                console.error('Failed to load zoning jobs:', error);
                setZoningJobs({ jobs: [], error: error.message || 'Unknown error' });
            })
            .finally(() => setLoadingJobs(false));
    };

    const getWatchKind = () => {
        const lower = watchContextCommand.toLowerCase();
        if (lower.includes('consolidate')) return 'consolidation';
        if (lower.includes('plan')) return 'plans';
        if (lower.includes('feature')) return 'features';
        return 'any';
    };

    const formatTimestamp = (value: any) => {
        if (!value) {
            return '-';
        }
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return String(value);
        }
        return date.toLocaleString();
    };

    const formatDuration = (seconds: any) => {
        const value = typeof seconds === 'number' ? seconds : Number(seconds);
        if (!Number.isFinite(value) || value <= 0) {
            return '-';
        }
        const mins = Math.floor(value / 60);
        const secs = value % 60;
        if (mins === 0) {
            return `${secs}s`;
        }
        if (secs === 0) {
            return `${mins}m`;
        }
        return `${mins}m ${secs}s`;
    };

    const withTimeout = <T,>(promise: Promise<T>, ms: number) =>
        Promise.race<T>([
            promise,
            new Promise<T>((_, reject) =>
                setTimeout(() => reject(new Error('Timeout while loading DB job')), ms)
            )
        ]);

    const loadZoningDbLatestJob = async () => {
        setLoadingDbJobs(true);
        const requestId = ++watchRequestIdRef.current;
        try {
            const data: any = await withTimeout(userAPI.getZoningDbLatestJob(getWatchKind()), 8000);
            if (requestId !== watchRequestIdRef.current) {
                return;
            }
            const job = data?.job || null;
            const errorMessage = data?.error ? String(data.error) : null;
            setZoningDbJobsError(errorMessage);
            if (!errorMessage) {
                setDbJobsLoadedOnce(true);
            }

            if (!errorMessage && job) {
                setWatchJobId(job._id);
                setWatchJob(job);
                if (job?.updatedAt) {
                    setWatchJobUpdatedAt(String(job.updatedAt));
                }
            } else {
                setWatchJobId(null);
                setWatchJob(null);
            }
        } catch (error) {
            if (requestId !== watchRequestIdRef.current) {
                return;
            }
            console.error('Failed to load zoning DB jobs:', error);
            setZoningDbJobsError(error instanceof Error ? error.message : String(error));
        } finally {
            if (requestId === watchRequestIdRef.current) {
                setLoadingDbJobs(false);
            }
        }
    };

    const _loadZoningDbJob = async (jobId: string) => {
        try {
            const data: any = await userAPI.getZoningDbJob(jobId);
            const job = data?.job || null;
            setWatchJob(job);
            if (job?.updatedAt) {
                setWatchJobUpdatedAt(String(job.updatedAt));
            }
            return job;
        } catch (error) {
            console.error('Failed to load zoning DB job:', error);
            setWatchJob(null);
            return null;
        }
    };

    const loadZoningLoopJobs = async () => {
        if (loadingLoopJobsRef.current) {
            return;
        }
        loadingLoopJobsRef.current = true;
        setLoadingLoopJobs(true);
        try {
            const results = await Promise.all(
                loopJobDefinitions.map(async (def) => {
                    try {
                        const data: any = await userAPI.getZoningLoopLatestJob(def.type);
                        return { type: def.type, job: data?.job || null, error: data?.error || null };
                    } catch (error) {
                        return {
                            type: def.type,
                            job: null,
                            error: error instanceof Error ? error.message : String(error),
                        };
                    }
                })
            );

            const nextJobs: Record<string, any> = {};
            const errors: string[] = [];
            results.forEach((result) => {
                if (result.job) {
                    nextJobs[result.type] = result.job;
                }
                if (result.error) {
                    errors.push(`${result.type}: ${result.error}`);
                }
            });

            setLoopJobs(nextJobs);
            setLoopJobsError(errors.length ? errors.join(' | ') : null);
            setLoopJobsLoadedOnce(true);
        } finally {
            setLoadingLoopJobs(false);
            loadingLoopJobsRef.current = false;
        }
    };

    const loadGermanyMap = () => {
        userAPI.getZoningGermanyMap()
            .then((data: any) => {
                if (data && data.svg) {
                    // Extract all path and group elements from the SVG
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(data.svg, "image/svg+xml");

                    // Cleanup paths to be transparent overlay
                    const allElements = doc.querySelectorAll('path, g, polygon, polyline');
                    allElements.forEach(el => {
                        (el as HTMLElement).style.fill = 'none';
                        (el as HTMLElement).style.stroke = '#000';
                        (el as HTMLElement).style.strokeWidth = '0.5px';
                        el.removeAttribute('fill'); // Remove any fill attribute
                    });

                    const paths = doc.querySelectorAll('path, g, polygon, polyline');
                    let pathsHtml = "";
                    paths.forEach(p => {
                        // Skip if it's already inside a group we've processed
                        if (p.parentElement?.tagName === 'svg') {
                            pathsHtml += p.outerHTML;
                        }
                    });
                    setGermanyMapPaths(pathsHtml);
                }
            })
            .catch(error => {
                console.error('Failed to load Germany map:', error);
            });
    };

    const loadZoningSVG = (resetCounts: boolean = true) => {
        setLoadingSVG(true);
        if (resetCounts) {
            setTileCounts({}); // Reset counts for fresh loading animation
        }

        userAPI.getZoningCoverageGrid()
            .then((grid: any) => {
                setZoningGrid(grid);
                setLastSVGUpdate(new Date());

                // Fetch counts in row-based chunks for "filling in" effect
                const chunkSize = 10;
                const rows = grid.rows;

                const fetchChunk = async (start: number) => {
                    if (start >= rows) {
                        setLoadingSVG(false);
                        return;
                    }
                    const end = Math.min(start + chunkSize - 1, rows - 1);
                    try {
                        const counts = await userAPI.getZoningCoverageCounts(start, end) as Record<string, number>;
                        setTileCounts(prev => ({ ...prev, ...counts }));
                        fetchChunk(start + chunkSize);
                    } catch (e) {
                        console.error(`Failed to fetch counts for rows ${start}-${end}`, e);
                        setLoadingSVG(false);
                    }
                };

                fetchChunk(0);
            })
            .catch(error => {
                console.error('Failed to load zoning grid:', error);
                setLoadingSVG(false);
            });
    };

    useEffect(() => {
        loadAgenticServices();
        loadUsageMetrics();
        loadZoningStats();
        loadZoningJobs();
        loadZoningLoopJobs();
        loadZoningSVG();
        loadGermanyMap();
        setZoningReloadAt(new Date());
        setZoningReloadSource("Initial");
    }, []);

    useEffect(() => {
        loadingZoningRef.current = loadingZoning;
    }, [loadingZoning]);

    useEffect(() => {
        loadingSVGRef.current = loadingSVG;
    }, [loadingSVG]);

    useEffect(() => {
        loadingLoopJobsRef.current = loadingLoopJobs;
    }, [loadingLoopJobs]);

    useEffect(() => {
        if (!showWatchDialog) return;

        loadZoningDbLatestJob();

        const interval = setInterval(() => {
            loadZoningDbLatestJob();
        }, 5000);

        return () => clearInterval(interval);
    }, [showWatchDialog, watchContextCommand]);

    useEffect(() => {
        if (showWatchDialog) {
            loadZoningDbLatestJob();
        }
    }, [showWatchDialog]);

    useEffect(() => {
        const statsInterval = setInterval(() => {
            if (loadingZoningRef.current) {
                return;
            }
            setZoningReloadAt(new Date());
            setZoningReloadSource("Auto (Stats)");
            loadZoningStats();
        }, 15000);

        const mapInterval = setInterval(() => {
            if (loadingSVGRef.current) {
                return;
            }
            setZoningReloadAt(new Date());
            setZoningReloadSource("Auto (Map)");
            loadZoningSVG(false);
        }, 15000);

        const loopInterval = setInterval(() => {
            if (loadingLoopJobsRef.current) {
                return;
            }
            loadZoningLoopJobs();
        }, 15000);

        return () => {
            clearInterval(statsInterval);
            clearInterval(mapInterval);
            clearInterval(loopInterval);
        };
    }, []);


    const filteredAgenticData = agenticData.filter((tool: any) => {
        if (!filter.trim()) return true;
        const lower = filter.trim().toLowerCase();
        return (tool.name?.toLowerCase().startsWith(lower) || tool.provider?.name?.toLowerCase().startsWith(lower));
    });

    console.log('MonitoringPanel Render:', { loading, loadingZoning, hasZoningStats: !!zoningStats, zoningStatsError: zoningStats?.error });

    if (!usageMetrics) {
        return (
            <div
                className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4"
                role="status"
                aria-live="polite"
            >
                <span className="sr-only">{t('global.loading')}</span>
                <Skeleton className="h-10 w-[420px] max-w-full" />
                <Skeleton className="h-[220px] w-full" />
                <Skeleton className="h-[220px] w-full" />
            </div>
        );
    }

    const chartConfigs = [
        {
            key: 'requestsInYear',
            title: 'Aktuelles Jahr',
            data: usageMetrics?.monthsInCurrentYear || [],
            length: 12,
        },
        {
            key: 'requestsInMonth',
            title: 'Aktueller Monat',
            data: usageMetrics?.daysInCurrentMonth || [],
            length: 31,
        },
        {
            key: 'requestsInDay',
            title: 'Aktueller Tag',
            data: usageMetrics?.hoursInCurrentDay || [],
            length: 24,
        },
        {
            key: 'requestsInHour',
            title: 'Aktuelle Stunde',
            data: usageMetrics?.minutesInCurrentHour || [],
            length: 60,
        },
        {
            key: 'requestsInMinute',
            title: 'Aktuelle Minute',
            data: usageMetrics?.currentMinute?.requests?.perSecond
                ? usageMetrics.currentMinute.requests.perSecond.map((count: number) => ({ count }))
                : [],
            length: 60,
        },
    ];

    const failedNoGeom = zoningStats?.stats?.['FAILED'] || 0;
    const failedConsolidation = zoningStats?.stats?.['FAILED_CONSOLIDATION'] || 0;
    const failedTotal = failedNoGeom + failedConsolidation;
    const harvestStats = zoningStats?.harvest || {};
    const harvestTotal = harvestStats.total || 0;
    const harvestNotProcessed = harvestStats.notProcessed || 0;
    const harvestExhaustedNoLayers = harvestStats.exhaustedNoLayers || 0;
    const harvestExhaustedZeroFeatures = harvestStats.exhaustedZeroFeatures || 0;
    const harvestExhaustedTotal = harvestExhaustedNoLayers + harvestExhaustedZeroFeatures;
    const harvestProcessedWithData = harvestStats.processedWithData || 0;

    const loopRows = loopJobDefinitions.map((def) => {
        const job = loopJobs[def.type];
        const stats = job?.stats || {};
        const startedAt = stats.lastCycleStartedAt;
        const finishedDuration = stats.lastCycleDurationSec;
        const inProgressDurationSec = startedAt && !finishedDuration
            ? Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000))
            : null;
        return {
            type: def.type,
            label: def.label,
            status: job?.status || '-',
            lastRunAt: formatTimestamp(stats.lastRunAt),
            lastHeartbeatAt: formatTimestamp(stats.lastHeartbeatAt || job?.updatedAt),
            lastCycleDuration: formatDuration(finishedDuration ?? inProgressDurationSec),
            cycles: stats.cycles ?? 0,
            errors: stats.errors ?? 0,
            details: def.buildDetails(stats),
            lastError: stats.lastError,
        };
    });

    const agenticFilterActive = filter.trim().length > 0;

    return (
        <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="max-w-full overflow-x-auto">
                    <TabsTrigger value="usage">Nutzung</TabsTrigger>
                    <TabsTrigger value="agentic">Agentic</TabsTrigger>
                    <TabsTrigger value="xplanung">XPlanung</TabsTrigger>
                    <TabsTrigger value="security">Sicherheit</TabsTrigger>
                </TabsList>

                <TabsContent value="usage">
                    <Section title="Nutzungsstatistiken">
                        <div className="mb-4 flex items-center justify-end">
                            <RefreshButton
                                label="Aktualisieren"
                                disabled={loading}
                                onClick={loadUsageMetrics}
                            />
                        </div>
                        {usageMetrics && (
                            <div className="min-w-0">
                                <h3 className="text-base font-semibold">Anfragen</h3>
                                <div className="mt-4 flex min-w-0 items-end gap-6 overflow-x-auto">
                                    {chartConfigs.map(({ key, title, data, length }, _idx) => {
                                        let xaxis = {};
                                        let paddedData = [];
                                        // Helper to normalize entries
                                        const normalize = (entry: any) => {
                                            if (!entry) return { count: 0 };
                                            if (typeof entry.count === 'number') return { count: entry.count };
                                            if (typeof entry.requests === 'number') return { count: entry.requests };
                                            // Fallback: sum all numeric properties (for debugging/robustness)
                                            const numericSum = Object.values(entry).reduce((sum: number, val) =>
                                                typeof val === 'number' ? sum + val : sum, 0);
                                            return { count: numericSum };
                                        };
                                        // Pad and normalize
                                        const padded = Array.from({ length }, (_, i) => data[i] || null);
                                        const normalized = padded.map(entry => entry ? (entry.count ?? entry.requests ?? 0) : 0);
                                        paddedData = padded.map(normalize);
                                        // X axis config
                                        const chartColors = [theme.tertiary, theme.secondary, theme.primary];
                                        const _barData = paddedData.map((d: any) => d.count);
                                        if (key === 'requestsInYear') {
                                            xaxis = {
                                                categories: Array.from({ length: 12 }, (_, i) => `${i + 1}`),
                                                labels: {
                                                    formatter: (val: string, i: number) => (i === 5 ? '6' : i === 11 ? '12' : ''),
                                                    rotate: 0
                                                }
                                            };
                                        } else if (key === 'requestsInMonth') {
                                            xaxis = {
                                                categories: Array.from({ length: 31 }, (_, i) => `${i + 1}`),
                                                labels: {
                                                    formatter: (val: string, i: number) => (i === 0 ? '1' : i === 14 ? '15' : i === 30 ? '31' : ''),
                                                    rotate: 0
                                                }
                                            };
                                        } else if (key === 'requestsInDay') {
                                            xaxis = {
                                                categories: Array.from({ length: 24 }, (_, i) => `${i}`),
                                                labels: {
                                                    formatter: (val: string, i: number) => (i === 0 ? '0' : i === 12 ? '12' : i === 23 ? '23' : ''),
                                                    rotate: 0
                                                }
                                            };
                                        } else if (key === 'requestsInHour' || key === 'requestsInMinute') {
                                            // For hour/minute, aggregate into 12 bins of 5
                                            const bins = Array.from({ length: 12 }, (_, i) => {
                                                const start = i * 5;
                                                const end = start + 5;
                                                return normalized.slice(start, end).reduce((sum, val) => sum + val, 0);
                                            });
                                            paddedData = bins.map(count => ({ count }));
                                            xaxis = {
                                                categories: ["5", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55", "60"],
                                                labels: {
                                                    formatter: (val: string) => (["15", "30", "45", "60"].includes(val) ? val : ''),
                                                    rotate: 0
                                                }
                                            };
                                        }
                                        return (
                                            <div key={key} className="flex min-w-[220px] flex-1 flex-col items-center">
                                                <div className="mb-2 text-sm font-bold">{title}</div>
                                                <Chart
                                                    options={{
                                                        chart: { type: 'bar', height: 200 },
                                                        colors: chartColors,
                                                        xaxis,
                                                        yaxis: {},
                                                        dataLabels: { enabled: false },
                                                        tooltip: {
                                                            x: { formatter: (val: any) => val },
                                                            y: { formatter: (val: any) => val }
                                                        }
                                                    }}
                                                    series={[{ name: 'Requests', data: paddedData.map((d: any) => d.count) }]}
                                                    type="bar"
                                                    height={200}
                                                    width="100%"
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* Active Users Section */}
                                <h3 className="mt-10 text-base font-semibold">Active Users</h3>
                                <div className="mt-4 flex min-w-0 items-end gap-6 overflow-x-auto">
                                    {chartConfigs.map(({ key, title, data: _data, length }, _idx) => {
                                        // Compute active users: login - logout (minimum 0)
                                        let loginData = (usageMetrics?.[key]?.login || usageMetrics?.[key + 's']?.login || []);
                                        let logoutData = (usageMetrics?.[key]?.logout || usageMetrics?.[key + 's']?.logout || []);
                                        // Fallback for legacy/flat structure
                                        if (!Array.isArray(loginData) && Array.isArray(usageMetrics?.[key])) {
                                            loginData = usageMetrics[key].map((entry: any) => entry?.login ?? 0);
                                        }
                                        if (!Array.isArray(logoutData) && Array.isArray(usageMetrics?.[key])) {
                                            logoutData = usageMetrics[key].map((entry: any) => entry?.logout ?? 0);
                                        }
                                        // Pad arrays to length
                                        loginData = Array.from({ length }, (_, i) => loginData?.[i] ?? 0);
                                        logoutData = Array.from({ length }, (_, i) => logoutData?.[i] ?? 0);
                                        const activeUsers = loginData.map((login: number, i: number) => Math.max(0, login - logoutData[i]));
                                        // X axis config (reuse from above)
                                        let xaxis = {};
                                        if (key === 'requestsInYear') {
                                            xaxis = {
                                                categories: Array.from({ length: 12 }, (_, i) => `${i + 1}`),
                                                labels: {
                                                    formatter: (val: string, i: number) => (i === 5 ? '6' : i === 11 ? '12' : ''),
                                                    rotate: 0
                                                }
                                            };
                                        } else if (key === 'requestsInMonth') {
                                            xaxis = {
                                                categories: Array.from({ length: 31 }, (_, i) => `${i + 1}`),
                                                labels: {
                                                    formatter: (val: string, i: number) => (i === 0 ? '1' : i === 14 ? '15' : i === 30 ? '31' : ''),
                                                    rotate: 0
                                                }
                                            };
                                        } else if (key === 'requestsInDay') {
                                            xaxis = {
                                                categories: Array.from({ length: 24 }, (_, i) => `${i}`),
                                                labels: {
                                                    formatter: (val: string, i: number) => (i === 0 ? '0' : i === 12 ? '12' : i === 23 ? '23' : ''),
                                                    rotate: 0
                                                }
                                            };
                                        } else if (key === 'requestsInHour' || key === 'requestsInMinute') {
                                            // For hour/minute, aggregate into 12 bins of 5
                                            const bins = Array.from({ length: 12 }, (_, i) => {
                                                const start = i * 5;
                                                const end = start + 5;
                                                return activeUsers.slice(start, end).reduce((sum: number, val: number) => sum + val, 0);
                                            });
                                            // Replace activeUsers with binned data
                                            activeUsers.splice(0, activeUsers.length, ...bins);
                                            xaxis = {
                                                categories: ["5", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55", "60"],
                                                labels: {
                                                    formatter: (val: string) => (["15", "30", "45", "60"].includes(val) ? val : ''),
                                                    rotate: 0
                                                }
                                            };
                                        }
                                        const chartColors = [theme.primary, theme.secondary, theme.tertiary];
                                        return (
                                            <div key={key} className="flex min-w-[220px] flex-1 flex-col items-center">
                                                <div className="mb-2 text-sm font-bold">{title}</div>
                                                <Chart
                                                    options={{
                                                        chart: { type: 'bar', height: 200 },
                                                        colors: chartColors,
                                                        xaxis,
                                                        yaxis: {},
                                                        dataLabels: { enabled: false },
                                                        tooltip: {
                                                            x: { formatter: (val: any) => val },
                                                            y: { formatter: (val: any) => val }
                                                        }
                                                    }}
                                                    series={[{ name: 'Active Users', data: activeUsers }]}
                                                    type="bar"
                                                    height={200}
                                                    width="100%"
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </Section>
                </TabsContent>

                <TabsContent value="agentic">
                    <Section title="Agentic Tools">
                        <div className="mb-4 flex min-w-0 flex-wrap items-end justify-between gap-4">
                            <div className="flex w-[250px] min-w-0 max-w-full flex-col gap-1.5">
                                <Label htmlFor={filterFieldId}>{t('billingPanel.filter')}</Label>
                                <Input
                                    id={filterFieldId}
                                    value={filter}
                                    onChange={(event) => setFilter(event.target.value || "")}
                                    placeholder={t('monitoringPanel.filterPlaceholder')}
                                />
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <Button
                                    variant="outline"
                                    className="max-sm:w-full"
                                    disabled={loading}
                                    onClick={async () => {
                                        try {
                                            setLoading(true);
                                            await administrationAPI.refreshAgenticToolCaches();
                                            loadAgenticServices();

                                            // Get cache status to show totals
                                            const cacheStatus = await administrationAPI.getCacheStatus();
                                            const totalTools = cacheStatus.publishedCount || 0;
                                            const totalCustomers = cacheStatus.customers?.length || 0;

                                            pushSuccess(`Werkzeug-Caches erfolgreich aktualisiert. ${totalTools} Werkzeuge für ${totalCustomers} Kunden gecacht.`);
                                        } catch (error) {
                                            console.error('Failed to refresh tool caches:', error);
                                            pushMessage("Fehler beim Aktualisieren der Werkzeug-Caches");
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                >
                                    {loading
                                        ? <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />
                                        : <Database aria-hidden className="mr-2 h-4 w-4" />}
                                    Werkzeug-Caches aktualisieren
                                </Button>
                                <RefreshButton
                                    label="Aktualisieren"
                                    disabled={loading}
                                    onClick={loadAgenticServices}
                                />
                            </div>
                        </div>
                        <div className="min-w-0 overflow-x-auto">
                            <DataTable
                                columns={agenticColumns}
                                data={filteredAgenticData}
                                loading={loading}
                                pageSize={5}
                                page={agenticServicesPagination.page}
                                onPageChange={agenticServicesPagination.onPageChange}
                                previousAriaLabel={t('global.back')}
                                nextAriaLabel={t('global.next')}
                                emptyMessage={
                                    agenticFilterActive ? (
                                        <TableEmptyState
                                            title={t('monitoringPanel.noMatchingAgenticToolsTitle')}
                                            hint={t('monitoringPanel.noMatchingAgenticToolsHint')}
                                            action={
                                                <Button variant="outline" size="sm" onClick={() => setFilter('')}>
                                                    {t('billingPanel.resetFilter')}
                                                </Button>
                                            }
                                        />
                                    ) : (
                                        <TableEmptyState
                                            title={t('monitoringPanel.noAgenticToolsTitle')}
                                            hint={t('monitoringPanel.noAgenticToolsHint')}
                                        />
                                    )
                                }
                            />
                        </div>
                    </Section>
                </TabsContent>

                <TabsContent value="xplanung">
                    <Section title="XPlanung Backlog & Monitoring">
                        {zoningStats && !zoningStats.error && (
                            <div className="flex min-w-0 flex-wrap gap-8">
                                <div className="flex min-w-0 flex-1 basis-[500px] flex-col gap-8">
                                    <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                                        <div className="min-w-0">
                                            <h3 className="text-base font-semibold">Statistiken & Kennzahlen</h3>
                                            <div className="text-xs text-muted-foreground">
                                                Letzte Aktualisierung: {zoningReloadAt ? zoningReloadAt.toLocaleTimeString() : "-"}
                                            </div>
                                        </div>
                                        <RefreshButton
                                            label="Statistiken aktualisieren"
                                            disabled={loadingZoning}
                                            onClick={() => {
                                                setZoningReloadAt(new Date());
                                                setZoningReloadSource("Manual (Stats)");
                                                loadZoningStats();
                                            }}
                                        />
                                    </div>
                                    <div className="flex min-w-0 flex-wrap gap-6">
                                        <div className="flex w-[200px] max-w-full flex-col items-center">
                                            <div className="mb-2 text-sm font-bold">Status Verteilung</div>
                                            <Chart
                                                options={{
                                                    chart: { type: 'donut' },
                                                    labels: ['Rohdaten', 'In Verarbeitung', 'Konsolidiert', 'Reparatur', 'Fehlgeschlagen'],
                                                    colors: [theme.secondary, theme.primary, theme.tertiary, '#ffc107', '#dc3545'],
                                                    legend: { show: false },
                                                    dataLabels: { enabled: false }
                                                }}
                                                series={[
                                                    zoningStats.stats['RAW'] || 0,
                                                    zoningStats.stats['PROCESSING'] || 0,
                                                    zoningStats.stats['CONSOLIDATED'] || 0,
                                                    zoningStats.stats['REPAIR_NEEDED'] || 0,
                                                    failedTotal
                                                ]}
                                                type="donut"
                                                height={150}
                                                width="100%"
                                            />
                                        </div>
                                        <div className="flex min-w-0 flex-1 flex-col">
                                            <div className="mb-2 text-sm font-bold">Kennzahlen Features</div>
                                            <div className="flex max-w-[350px] flex-wrap gap-5">
                                                <Metric title="Gesamt" value={zoningStats.total?.toLocaleString()} color="#605e5c" />
                                                <Metric title="Rohdaten" value={(zoningStats.stats['RAW'] || 0).toLocaleString()} color={theme.secondary} />
                                                <div className="h-0 basis-full"></div>
                                                <Metric title="In Verarbeitung" value={(zoningStats.stats['PROCESSING'] || 0).toLocaleString()} color={theme.primary} />
                                                <Metric title="Konsolidiert" value={(zoningStats.stats['CONSOLIDATED'] || 0).toLocaleString()} color={theme.tertiary} />
                                                <div className="h-0 basis-full"></div>
                                                <Metric title="Reparatur" value={(zoningStats.stats['REPAIR_NEEDED'] || 0).toLocaleString()} color="#ffc107" />
                                                <Metric title="Fehlgeschlagen" value={failedTotal.toLocaleString()} color="#dc3545" />
                                            </div>
                                        </div>
                                    </div>

                                    {zoningStats.discovery && (
                                        <div className="flex min-w-0 flex-wrap gap-6 pt-8">
                                            <div className="flex w-[200px] max-w-full flex-col items-center">
                                                <div className="mb-2 text-sm font-bold">Discovery Status</div>
                                                <Chart
                                                    options={{
                                                        chart: { type: 'donut' },
                                                        labels: ['Verifiziert', 'Neu Entdeckt', 'Fehlgeschlagen'],
                                                        colors: [theme.tertiary, theme.secondary, '#dc3545'],
                                                        legend: { show: false },
                                                        dataLabels: { enabled: false }
                                                    }}
                                                    series={[
                                                        zoningStats.discovery.stats['VERIFIED'] || 0,
                                                        zoningStats.discovery.stats['NEW'] || 0,
                                                        zoningStats.discovery.stats['FAILED'] || 0
                                                    ]}
                                                    type="donut"
                                                    height={150}
                                                    width="100%"
                                                />
                                            </div>
                                            <div className="flex min-w-0 flex-1 flex-col">
                                                <div className="mb-2 text-sm font-bold">Kennzahlen Quellen</div>
                                                <div className="flex max-w-[350px] flex-wrap gap-5">
                                                    <Metric title="Gefunden (Total)" value={zoningStats.discovery.total?.toLocaleString()} color={theme.primary} />
                                                    <Metric title="Verifiziert" value={(zoningStats.discovery.stats['VERIFIED'] || 0).toLocaleString()} color={theme.tertiary} />
                                                    <div className="h-0 basis-full"></div>
                                                    <Metric title="Neu Entdeckt" value={(zoningStats.discovery.stats['NEW'] || 0).toLocaleString()} color={theme.secondary} />
                                                    <Metric title="Fehlgeschlagen" value={(zoningStats.discovery.stats['FAILED'] || 0).toLocaleString()} color="#dc3545" />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {zoningStats.harvest && (
                                        <div className="flex min-w-0 flex-wrap gap-6 pt-8">
                                            <div className="flex w-[200px] max-w-full flex-col items-center">
                                                <div className="mb-2 text-sm font-bold">Extraktion Status</div>
                                                <Chart
                                                    options={{
                                                        chart: { type: 'donut' },
                                                        labels: ['Nicht verarbeitet', 'Erschöpft', 'Verarbeitet mit Daten'],
                                                        colors: [theme.secondary, '#ff9800', theme.tertiary],
                                                        legend: { show: false },
                                                        dataLabels: { enabled: false }
                                                    }}
                                                    series={[
                                                        harvestNotProcessed,
                                                        harvestExhaustedTotal,
                                                        harvestProcessedWithData
                                                    ]}
                                                    type="donut"
                                                    height={150}
                                                    width="100%"
                                                />
                                            </div>
                                            <div className="flex min-w-0 flex-1 flex-col">
                                                <div className="mb-2 text-sm font-bold">Kennzahlen Extraktion</div>
                                                <div className="flex max-w-[420px] flex-wrap gap-5">
                                                    <Metric title="Gesamt" value={harvestTotal.toLocaleString()} color="#605e5c" />
                                                    <Metric title="Nicht verarbeitet" value={harvestNotProcessed.toLocaleString()} color={theme.secondary} />
                                                    <Metric title="Erschöpft (Total)" value={harvestExhaustedTotal.toLocaleString()} color="#ff9800" />
                                                    <div className="h-0 basis-full"></div>
                                                    <Metric title="Erschöpft: keine Layer" value={harvestExhaustedNoLayers.toLocaleString()} color="#ffb74d" />
                                                    <Metric title="Erschöpft: 0 Features" value={harvestExhaustedZeroFeatures.toLocaleString()} color="#ffa726" />
                                                    <div className="h-0 basis-full"></div>
                                                    <Metric title="Verarbeitet mit Daten" value={harvestProcessedWithData.toLocaleString()} color={theme.tertiary} />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex min-w-0 flex-1 basis-[400px] flex-col">
                                    <div className="mb-2 flex min-w-0 flex-wrap items-center justify-between gap-2">
                                        <h3 className="text-base font-semibold">Geodaten-Abdeckung (Heatmap)</h3>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                aria-pressed={showMapOverlay}
                                                onClick={() => setShowMapOverlay(!showMapOverlay)}
                                            >
                                                <Layers aria-hidden className="mr-2 h-4 w-4" />
                                                {showMapOverlay ? 'Karte ausblenden' : 'Karte einblenden'}
                                            </Button>
                                            <RefreshButton
                                                label="Heatmap aktualisieren"
                                                disabled={loadingSVG}
                                                onClick={() => {
                                                    setZoningReloadAt(new Date());
                                                    setZoningReloadSource("Manual (Map)");
                                                    loadZoningSVG();
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {loadingSVG && (
                                        <div className="text-sm text-muted-foreground" role="status">
                                            Lade Daten ({Object.keys(tileCounts).length} Kacheln)...
                                        </div>
                                    )}
                                    {zoningGrid ? (
                                        <div className="max-h-[450px] w-full overflow-hidden rounded-md border bg-card p-3 shadow-sm">
                                            <svg
                                                width="100%"
                                                height="auto"
                                                viewBox={`0 0 ${zoningGrid.cols * 10} ${zoningGrid.rows * 10}`}
                                                xmlns="http://www.w3.org/2000/svg"
                                                preserveAspectRatio="xMidYMid meet"
                                                role="img"
                                                aria-label="Geodaten-Abdeckung Deutschlands als Kachelraster"
                                            >
                                                <rect width="100%" height="100%" fill="#f8f9fa" />
                                                {zoningGrid.tiles.map((t: any) => {
                                                    const count = tileCounts[t.id];
                                                    const isLoaded = count !== undefined;
                                                    let color = "#e9ecef"; // Grey for unloaded

                                                    if (isLoaded) {
                                                        if (count === 0) color = "#f8f9fa";
                                                        else if (count < 5) color = "#f7fcf5";
                                                        else if (count < 15) color = "#e5f5e0";
                                                        else if (count < 40) color = "#c7e9c0";
                                                        else if (count < 100) color = "#a1d99b";
                                                        else if (count < 200) color = "#74c476";
                                                        else if (count < 400) color = "#41ab5d";
                                                        else if (count < 650) color = "#238b45";
                                                        else if (count < 900) color = "#006d2c";
                                                        else color = "#00441b";
                                                    }

                                                    return (
                                                        <rect
                                                            key={t.id}
                                                            x={t.col * 10}
                                                            y={(zoningGrid.rows - 1 - t.row) * 10}
                                                            width={9.8}
                                                            height={9.8}
                                                            fill={color}
                                                            style={{ transition: 'fill 0.3s ease' }}
                                                        >
                                                            <title>{isLoaded ? `${count} ${count === 1 ? 'Eintrag' : 'Einträge'}` : 'Wird geladen...'}</title>
                                                        </rect>
                                                    );
                                                })}
                                                {showMapOverlay && germanyMapPaths && (
                                                    <g
                                                        dangerouslySetInnerHTML={{ __html: germanyMapPaths }}
                                                        style={{
                                                            pointerEvents: 'none',
                                                            opacity: 0.6,
                                                            stroke: '#333',
                                                            strokeWidth: '0.5px'
                                                        }}
                                                        transform={`scale(${(zoningGrid.cols * 10) / 591.504}, ${(zoningGrid.rows * 10) / 800.504})`}
                                                    />
                                                )}
                                            </svg>
                                        </div>
                                    ) : (
                                        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
                                            {loadingSVG ? "Warten auf Server..." : "Heatmap konnte nicht geladen werden."}
                                        </div>
                                    )}
                                    <div className="mt-4 flex min-w-0 flex-col gap-1">
                                        <div className="mb-0.5 text-xs font-bold uppercase text-muted-foreground">Feature-Dichte</div>
                                        <div className="flex w-full flex-wrap">
                                            {[
                                                { range: '0', color: '#f8f9fa' },
                                                { range: '1-4', color: '#f7fcf5' },
                                                { range: '5-14', color: '#e5f5e0' },
                                                { range: '15-39', color: '#c7e9c0' },
                                                { range: '40-99', color: '#a1d99b' },
                                                { range: '100-199', color: '#74c476' },
                                                { range: '200-399', color: '#41ab5d' },
                                                { range: '400-649', color: '#238b45' },
                                                { range: '650-899', color: '#006d2c' },
                                                { range: '900+', color: '#00441b' }
                                            ].map((item, idx) => (
                                                <div key={idx} className="mb-3 mr-3 flex w-[90px] shrink-0 items-center">
                                                    <svg width="14" height="14" className="block shrink-0" aria-hidden>
                                                        <rect
                                                            x="0.5"
                                                            y="0.5"
                                                            width="13"
                                                            height="13"
                                                            fill={item.color}
                                                            rx="2"
                                                            ry="2"
                                                            stroke="#ccc"
                                                            strokeWidth="1"
                                                        />
                                                    </svg>
                                                    <div className="ml-2.5 whitespace-nowrap text-xs font-bold leading-[14px] text-muted-foreground">
                                                        {item.range}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-2 text-xs text-muted-foreground">
                                            Letztes Update: {lastSVGUpdate ? lastSVGUpdate.toLocaleTimeString() : '-'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mt-12 pt-8">
                            <div className="mb-4 flex min-w-0 flex-wrap items-center justify-between gap-2">
                                <h3 className="text-base font-semibold">Orchestrator Loops</h3>
                                <RefreshButton
                                    label="Loop-Jobs aktualisieren"
                                    disabled={loadingLoopJobs}
                                    onClick={loadZoningLoopJobs}
                                />
                            </div>
                            <div className="mb-2 text-sm text-muted-foreground">
                                {loadingLoopJobs && !loopJobsLoadedOnce
                                    ? 'Lade Loop-Jobs...'
                                    : loadingLoopJobs && loopJobsLoadedOnce
                                        ? 'Aktualisiere...'
                                        : loopJobsError
                                            ? `Fehler: ${loopJobsError}`
                                            : 'Loop-Status aus Job-Objekten'}
                            </div>
                            <div className="min-w-0 overflow-x-auto">
                                <DataTable
                                    columns={loopJobColumns}
                                    data={loopRows}
                                    pageSize={10}
                                    previousAriaLabel={t('global.back')}
                                    nextAriaLabel={t('global.next')}
                                    emptyMessage={
                                        <TableEmptyState
                                            title="Keine Loop-Jobs gefunden."
                                            hint={t('monitoringPanel.noLoopJobsHint')}
                                        />
                                    }
                                />
                            </div>
                        </div>

                        {zoningStats && (
                            <div className="mt-4 text-center text-sm text-muted-foreground">
                                Statistiken zuletzt aktualisiert: {new Date(zoningStats.timestamp).toLocaleString()}
                            </div>
                        )}
                        {zoningStats && zoningStats.error && (
                            <Alert variant="destructive" className="mt-4">
                                <AlertDescription className="min-w-0 break-words">
                                    Fehler beim Laden der XPlanung-Daten: {zoningStats.error}
                                </AlertDescription>
                            </Alert>
                        )}
                        {loadingZoning && (
                            <div className="p-8 text-center text-sm text-muted-foreground" role="status">
                                {t('global.loading')}...
                            </div>
                        )}
                        {!zoningStats && !loadingZoning && (
                            <div className="p-8 text-center text-sm text-muted-foreground">
                                {t('billingPanel.noDataAvailable')}
                            </div>
                        )}
                    </Section>
                </TabsContent>

                <TabsContent value="security">
                    <SecurityPanel />
                </TabsContent>
            </Tabs>
            <Dialog
                open={showExamplesDialog}
                onOpenChange={(open) => { if (!open) setShowExamplesDialog(false); }}
            >
                <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[600px]">
                    <DialogHeader>
                        <DialogTitle>Beispielanfragen</DialogTitle>
                    </DialogHeader>
                    <ul className="list-disc space-y-1 pl-5 text-sm">
                        {currentExamples.map((ex, idx) => (
                            <li key={idx} className="min-w-0 break-words">{ex}</li>
                        ))}
                    </ul>
                    <DialogFooter>
                        <Button className="max-sm:w-full" onClick={() => setShowExamplesDialog(false)}>
                            {t('global.close')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog
                open={showWatchDialog}
                onOpenChange={(open) => {
                    if (!open) {
                        setShowWatchDialog(false);
                        setWatchJob(null);
                        setWatchJobId(null);
                    }
                }}
            >
                <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[720px]">
                    <DialogHeader>
                        <DialogTitle>DB Job Monitor</DialogTitle>
                    </DialogHeader>
                    <div className="flex min-w-0 flex-col gap-4">
                        <div className="min-w-0 break-words text-sm text-muted-foreground">
                            {loadingDbJobs && !dbJobsLoadedOnce
                                ? 'Lade DB-Jobs...'
                                : loadingDbJobs && dbJobsLoadedOnce
                                    ? 'Aktualisiere...'
                                    : zoningDbJobsError
                                        ? `Fehler: ${zoningDbJobsError}`
                                        : watchJobId
                                            ? `Ausgewählter Job: ${watchJobId}`
                                            : 'Kein DB-Job gefunden.'}
                        </div>
                        {!watchJob && watchJobId && (
                            <div className="min-w-0 break-words text-sm text-muted-foreground">
                                Warte auf Job-Daten{watchJobUpdatedAt ? ` (zuletzt: ${watchJobUpdatedAt})` : ''}...
                            </div>
                        )}
                        <DbJobLogsView data={watchJob} />
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            className="max-sm:w-full"
                            onClick={() => setShowWatchDialog(false)}
                        >
                            {t('global.close')}
                        </Button>
                        <Button
                            className="max-sm:w-full"
                            disabled={loadingDbJobs}
                            onClick={() => loadZoningDbLatestJob()}
                        >
                            {loadingDbJobs && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                            {t('global.refresh')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MonitoringPanel;
